import { useEffect, useContext } from 'react';
import ApiService from '../../../../services/ApiService';
import { FeedbackContext } from '../../../../contexts/FeedbackContext';
import { InvoiceItem } from './types';

interface Params {
  items: InvoiceItem[];
  setItems: React.Dispatch<React.SetStateAction<InvoiceItem[]>>;
  cfops: { id: number; code: string; description: string }[];
  setCfopsPerItem: React.Dispatch<React.SetStateAction<Map<number, { id: number; code: string; description: string }[]>>>;
  destStateId: number | null;
  isEmitida: boolean;
  companyEmissorId: number | null;
}

export function useItemTaxResolution({
  items,
  setItems,
  cfops,
  setCfopsPerItem,
  destStateId,
  isEmitida,
  companyEmissorId,
}: Params) {
  const { setFeedback } = useContext(FeedbackContext);

  // Backfill do cfopId a partir do código (string) quando a lista de CFOPs carrega
  useEffect(() => {
    if (cfops.length === 0) return;
    setItems((prev) =>
      prev.map((p) => {
        if (p.cfopId || !p.cfop) return p;
        const match = cfops.find((c) => c.code.toString() === String(p.cfop));
        return match ? { ...p, cfopId: match.id } : p;
      })
    );
  }, [cfops, setItems]);

  // CFOPs filtrados por NCM + UF + emissor; auto-seleciona quando há resultado único.
  // Sem emissor selecionado não busca — o cenário depende do emissor (ver aviso no form).
  useEffect(() => {
    if (!destStateId || !companyEmissorId) return;
    const itemsWithNcm = items.filter((item) => item.ncmId);
    if (itemsWithNcm.length === 0) return;

    itemsWithNcm.forEach(async (item) => {
      try {
        const response = await ApiService.fiscal.getCfops({
          ncmId: item.ncmId ?? undefined,
          stateId: destStateId,
          companyEmissorId,
        });
        const data = Array.isArray(response.data) ? response.data : response.data?.data;
        // Entrada sempre gravada (mesmo vazia): o picker distingue "sem cenário" de "ainda não buscou".
        const lista = data || [];
        setCfopsPerItem((prev) => new Map(prev).set(item.id, lista));
        if (lista.length === 1 && !item.cfopId) {
          const cfop = lista[0];
          setItems((prev) =>
            prev.map((p) =>
              p.id === item.id ? { ...p, cfopId: cfop.id, cfop: String(cfop.code) } : p
            )
          );
        }
      } catch {
        // silencioso — fallback para lista global
      }
    });
  }, [destStateId, companyEmissorId, items.map((i) => i.ncmId).join(',')]);

  // Lookup fiscal completo (CST, alíquotas) quando ncmId + cfopId + destStateId + emissor definidos
  useEffect(() => {
    if (!destStateId || isEmitida || !companyEmissorId) return;

    items.forEach(async (item) => {
      if (!item.ncmId || !item.cfopId) return;

      const currentHash = `${item.ncmId}-${item.cfopId}-${destStateId}-${companyEmissorId}`;
      if (item._lastLookupHash === currentHash) return;

      try {
        const res = await ApiService.fiscal.lookup({
          ncmId: item.ncmId,
          cfopId: item.cfopId,
          stateId: destStateId,
          companyEmissorId,
        });
        const { taxConfig, taxScenario } = res.data;

        setItems((prev) =>
          prev.map((p) => {
            if (p.id !== item.id) return p;

            const parseBrl = (val: string) =>
              Number(val?.replace(/\./g, '')?.replace(',', '.') || 0);
            const fmtBrl = (val: number) =>
              val.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

            const aliqICMS = taxScenario.aliquotaICMS || 0;
            const aliqIPI = taxConfig.aliquotaIPI || taxScenario.aliquotaIPI || 0;
            const bcIcmsNum = parseBrl(p.bcIcms || p.vlTotal);
            const vlTotalNum = parseBrl(p.vlTotal);

            return {
              ...p,
              cst: taxScenario.cstICMS,
              cfop: taxConfig.cfop || p.cfop,
              aliqIcms: aliqICMS,
              aliqIpi: aliqIPI,
              vlIcms: fmtBrl((bcIcmsNum * aliqICMS) / 100),
              vlIpi: fmtBrl((vlTotalNum * aliqIPI) / 100),
              cstPisCofins: taxConfig.cstPIS_COFINS || p.cstPisCofins,
              cstIbsCbs: taxScenario.cstIBS_CBS || p.cstIbsCbs,
              classificacao: taxScenario.classificacaoTributaria || p.classificacao,
              vlIbs: fmtBrl((vlTotalNum * (taxScenario.aliquotaIBS || 0)) / 100),
              vlCbs: fmtBrl((vlTotalNum * (taxScenario.aliquotaCBS || 0)) / 100),
              _lastLookupHash: currentHash,
            };
          })
        );
      } catch {
        setFeedback({
          type: 'error',
          message: 'Nenhum cenário fiscal encontrado para esta combinação de NCM, CFOP e Estado.',
          isOpen: true,
          statusOk: false,
        });
        // Limpa o preview — valores da combinação anterior não valem para a nova seleção.
        setItems((prev) =>
          prev.map((p) =>
            p.id === item.id
              ? {
                  ...p,
                  cst: '',
                  vlIcms: '',
                  vlIpi: '',
                  cstPisCofins: '',
                  cstIbsCbs: '',
                  classificacao: '',
                  vlIbs: '',
                  vlCbs: '',
                  _lastLookupHash: currentHash,
                }
              : p
          )
        );
      }
    });
  }, [items, destStateId, isEmitida, companyEmissorId]);
}
